// TimeTrack (timesheet round): a row of a timeline that places things by their minutes along the span
// (useTimeSpan) and stacks the ones that overlap into rows, the way BoardLane does: the same placement
// (spanPos) and stacking (laneStacks). The timesheet's strip and rows pass stack={false}, since a day's
// parts never overlap. Each thing is drawn by renderItem, told where it falls (fractions of the track,
// 0 to 1) and its row; something with no end runs on to the track's end. Children are drawn under them
// (NowLine, a midnight line). Reads only --ttrack-*.
import { Fragment, type ReactNode } from 'react';
import { laneStacks } from '../BoardLane/BoardLane.tsx';
import { spanHours, spanPct, spanPos, useTimeSpan } from '../time-span.tsx';
import './TimeTrack.css';

export interface TrackItem {
  id: string;
  /** Minutes from the day's midnight; 1440 and on are the next day's. */
  start: number;
  /** Null while it's open: it runs on to the track's end. */
  end: number | null;
}

export interface TimeTrackProps<T extends TrackItem> {
  items: readonly T[];
  renderItem: (item: T, at: { from: number; to: number }, row: number) => ReactNode;
  /** What the row is, for a screen reader ("Clocked", "Fri Oct 23"). */
  label: string;
  /** Overlaps go into rows of their own (default); off, everything shares one row. */
  stack?: boolean;
  /** At least this many rows, so tracks side by side line up. */
  rows?: number;
  /** Drawn under the items: NowLine, a midnight line. */
  children?: ReactNode;
  /** The hour grid behind it (default on). */
  grid?: boolean;
}

/** A minute along the span as a fraction, 0 to 1. */
const frac = (span: { from: number; to: number }, m: number) => spanPct(span, m) / 100;

export function TimeTrack<T extends TrackItem>({
  items,
  renderItem,
  label,
  stack = true,
  rows: min = 1,
  children,
  grid = true,
}: TimeTrackProps<T>) {
  const span = useTimeSpan();
  const placed = items.map((it) => ({ it, start: it.start, end: it.end ?? span.to }));
  const stacks = stack ? laneStacks(placed) : [placed];
  while (stacks.length < min) stacks.push([]);
  const hours = spanHours(span).length - 1 || 1;
  return (
    <div
      className="fl-ttrack"
      role="group"
      aria-label={label}
      data-density={span.density}
      data-grid={grid ? '' : undefined}
      style={grid ? { backgroundSize: `${100 / hours}% 100%` } : undefined}
    >
      {children}
      {stacks.map((row, r) => (
        <div key={r} className="fl-ttrack__row">
          {row.map(({ it }) => (
            <Fragment key={it.id}>
              {renderItem(
                it,
                { from: frac(span, it.start), to: it.end === null ? 1 : frac(span, it.end) },
                r,
              )}
            </Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A dashed line across the track at a minute (midnight on a late call), with its words. */
export function TrackMark({ minute, label }: { minute: number; label: string }) {
  const span = useTimeSpan();
  if (minute < span.from || minute > span.to) return null;
  return (
    // past the middle its words go before the line, so they stay on the track
    <span
      className="fl-ttrack__mark"
      data-side={spanPct(span, minute) > 60 ? 'end' : undefined}
      style={spanPos(span, minute, minute)}
    >
      <span className="fl-ttrack__mark-words">{label}</span>
    </span>
  );
}
